import cn from 'classnames';
import { FC, useRef } from 'react';

import {
  MedicalImageRender,
  type ToothSliceGeometryMode,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';

import {
  MPRTitle,
  MPRToolsBar,
  MPR_PANE_LAYOUT_PAGE_VIEWER,
  useAddFullHeadMprViewToReport,
  useMPRCommands,
  useMPRShortcuts,
  type DCCTVolumeState,
  type MPRAnalyticsScreen,
} from '@/features/MPR';

import { FULL_HEAD_MPR_TOOLS } from '../../config';
import {
  useFullHeadMPR,
  useFullHeadMPRTools,
  useFullHeadMprEditor,
  useFullHeadMprSlicesContext,
  useResolvedToothID,
} from '../../hooks';
import {
  resolveFullHeadMprCursorIcon,
  type ToothSliceModeScope,
} from '../../lib';
import viewerStyles from '../FullHeadMPR.module.scss';
import { FullHeadMPRPaneControls } from '../FullHeadMPRPaneControls';
import { FullHeadMprSliceSessionPane } from '../FullHeadMprSliceSessionPane';
import { FullHeadMPRStatus } from '../FullHeadMPRStatus';
import { FullHeadMprToolCursorBadge } from '../FullHeadMprToolCursorBadge';

import styles from './FullHeadMPRPanel.module.scss';

// The slice modal has its own scope; the page panel renders the other two.
type FullHeadMPRPanelScope = Exclude<ToothSliceModeScope, 'modal'>;

type FullHeadMPRPanelVariant = {
  analyticsScreen: MPRAnalyticsScreen;
  defaultGeometryMode: ToothSliceGeometryMode;
  sliceModeScope: FullHeadMPRPanelScope;
  viewerClassName: string;
};

const PANEL_VARIANTS: Record<FullHeadMPRPanelScope, FullHeadMPRPanelVariant> = {
  tooth: {
    analyticsScreen: 'toothPage',
    defaultGeometryMode: 'series',
    sliceModeScope: 'tooth',
    viewerClassName: styles.toothViewer,
  },
  volume: {
    analyticsScreen: 'reportPage',
    defaultGeometryMode: 'series',
    sliceModeScope: 'volume',
    viewerClassName: styles.volumeViewer,
  },
};

type FullHeadMPRPanelProps = {
  variant: FullHeadMPRPanelScope;
  volumeState: DCCTVolumeState;
  className?: string;
  toothID?: string;
};

export const FullHeadMPRPanel: FC<FullHeadMPRPanelProps> = (props) => {
  const { variant, volumeState, className, toothID } = props;

  const containerRef = useRef<HTMLDivElement>(null);

  const {
    analyticsScreen,
    defaultGeometryMode,
    sliceModeScope,
    viewerClassName,
  } = PANEL_VARIANTS[variant];

  const analyticsToothID = useResolvedToothID(toothID);

  const {
    errorMessage,
    isReady,
    resetView,
    retry,
    setToothSliceGeometryMode,
    toothMode,
  } = useFullHeadMPR({
    containerRef,
    isActive: true,
    sliceModeScope,
    volumeState,
    defaultGeometryMode,
    toothID,
  });

  const editor = useFullHeadMprEditor({ isReady, toothID: toothID ?? '' });
  const {
    activeMode,
    annotations,
    imageOptions,
    handleToolClick,
    isAxesVisible,
    isImageOptionsSynced,
    isZoomSynced,
    setAxesVisible,
    setImageOptionsSynced,
    setZoomSynced,
  } = useFullHeadMPRTools({
    isReady,
    canEditAnnotations: editor.canEditAnnotations,
    canEditImageOptions: editor.canEditImageOptions,
    onReset: resetView,
  });
  const commands = useMPRCommands({
    annotations,
    imageOptions,
    isAxesVisible,
    isImageOptionsSynced,
    isZoomSynced,
    screen: analyticsScreen,
    tools: FULL_HEAD_MPR_TOOLS,
    toothID: analyticsToothID || undefined,
    isDisabled: !isReady,
    isDownloadDisabled: editor.isDownloadDisabled,
    onAxesVisibleChange: setAxesVisible,
    onDownload: editor.download,
    onImageOptionsSyncedChange: setImageOptionsSynced,
    onStepSlice: (direction) =>
      MedicalImageRender.stepFullHeadMPRSlice(direction),
    onToolClick: handleToolClick,
    onZoomSyncedChange: setZoomSynced,
  });
  const cursorIcon = resolveFullHeadMprCursorIcon({
    activeMode,
    annotationTool: annotations.activeTool,
    imageTool: imageOptions.activeTool,
  });
  const shortcuts = useMPRShortcuts({
    commands,
    isEnabled: isReady && !editor.isCheckingSignature,
  });

  const { resolveViewSelection } = useFullHeadMprSlicesContext();
  const addToReport = useAddFullHeadMprViewToReport({
    toothID: toothID ?? '',
    volumeFileID:
      volumeState.status === 'ready' ? volumeState.volumeFileID : null,
    resolveSelection: resolveViewSelection,
  });

  return (
    <div className={className} {...shortcuts}>
      <MPRToolsBar
        activeMode={activeMode}
        isAxesVisible={isAxesVisible}
        isImageOptionsSynced={isImageOptionsSynced}
        isZoomSynced={isZoomSynced}
        screen={analyticsScreen}
        tools={FULL_HEAD_MPR_TOOLS}
        commands={commands}
        shortcutsEnabled
        toothID={analyticsToothID || undefined}
        paneLayout={MPR_PANE_LAYOUT_PAGE_VIEWER}
        standardPlanesMode={{
          isActive: toothMode.geometryMode === 'orthogonal',
          isAvailable: toothMode.isActive,
          onChange: (isStandardPlanesEnabled) =>
            setToothSliceGeometryMode(
              isStandardPlanesEnabled ? 'orthogonal' : 'series',
            ),
        }}
        isDisabled={!isReady}
        onAxesVisibleChange={setAxesVisible}
        onImageOptionsSyncedChange={setImageOptionsSynced}
        onToolClick={handleToolClick}
        onZoomSyncedChange={setZoomSynced}
        renderLayout={({ renderToolbar }) => (
          <>
            {variant === 'tooth' && <MPRTitle />}
            {renderToolbar()}
          </>
        )}
      />

      <div className={styles.container} data-component="full-head-mpr-panel">
        <div
          ref={containerRef}
          data-mpr-viewport
          {...(!isReady && { inert: '' })}
          aria-hidden={!isReady}
          className={cn(
            styles.viewer,
            viewerClassName,
            !isReady && viewerStyles.preparing,
          )}
        />

        <FullHeadMPRStatus
          errorMessage={errorMessage}
          isReady={isReady}
          isSingleSkeleton
          onRetry={retry}
        />
        <FullHeadMprToolCursorBadge
          containerRef={containerRef}
          icon={isReady ? cursorIcon : null}
        />
      </div>

      <FullHeadMPRPaneControls isReady={isReady} />
      {editor.panes.map((pane) => (
        <FullHeadMprSliceSessionPane
          key={pane.projection}
          pane={pane}
          addToReport={
            toothID && isReady
              ? addToReport.getAddToReportProps({
                  isViewReady: pane.isReady,
                  snapshot: pane.snapshot,
                })
              : undefined
          }
          isAnnotationsHidden={!annotations.isVisible}
        />
      ))}
    </div>
  );
};
